<template>
  <info-panel
    v-if="momentum"
    :item="momentum"
    :title="$t('labels.momentumOfX', [element?.title])"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{ $t("labels.showEmptySections") }}</template>
    
    <template #image v-if="imageData">
      <img :src="imageData" />
    </template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template #description v-if="settingsValues.showEmptySections || !isEmpty(momentum.description)">
      <formatted-read-more-text
        :value="momentum.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <template #label-element>
      {{ $t("objectNames.element") }}
    </template>
    <template #element>
      <button-item v-if="element" @click="openOverlay(element)">
        {{ element?.title }}
      </button-item>
    </template>

    <template #label-value>
      {{ $t("labels.value") }}
    </template>
    <template #value>
      <span>{{ $t("words.startValue") }}</span
      >:
      <span>{{ formatNumber(startValue, "displaySign") }} {{ element?.unit }}</span>
    </template>

    <template #label-interval>
      {{ $t("labels.interval") }}
    </template>
    <template #interval>
      <span v-if="momentum.interval === 0">{{ $t("labels.everyRound") }}</span>
      <span v-if="momentum.interval != 0">
        {{ $t("labels.everyXRound", [momentum.interval]) }}
      </span>
    </template>

    <!-- INFLUENCE CURVE -->
    <template #label-curve>
      {{ $t("labels.graph") }}
    </template>
    <template #curve v-if="element && momentum">
      <div class="relation-infopanel__curverow">
        <input-curve
          class="momentum-infopanel__curve"
          :value="element.value"
          :x-min="element.minValue"
          :x-max="element.maxValue"
          :point-list="curvePoints"
          :type="curveType"
          :type-editable="false"
          :curve-editable="false"
          :formula="momentum.formula"
          @hovervalue="updateCurveHover($event)"
        />
        <div>
          <p>
            {{ $t("labels.xAxis") }}{{ $t(":") }}
            {{ $t("labels.valueOfX", [element.title || $t("labels.unnamed")])
            }}<br />
            {{ $t("labels.yAxis") }}{{ $t(":") }}
            {{ $t("labels.influenceOnX", [element.title || $t("labels.unnamed")]) }}
          </p>
    
          <i18n-t
            keypath="texts.influenceCurveConstantExample"
            tag="p"
            scope="global"
          >
            <template #startElement>
              <em>{{ element.title || $t("labels.unnamed") }}</em>
            </template>
            <template v-if="element.unit" #startUnit> {{ element.unit }} </template>
            <template #endElement>
              <em>{{ element.title || $t("labels.unnamed") }}</em>
            </template>
            <template #startValue>{{ curveHover.x }}</template>
            <template #impulseValue>
              {{ $n(curveHover.y || 0, "displaySign") }}
            </template>
            <template v-if="element.unit" #endUnit> {{ element.unit }} </template>
          </i18n-t>
        </div>
      </div>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { formatNumber } from "@/helpers/utility/Formatting";
import { isEmpty } from "@/helpers/utility/Text";
import { mapValues } from "@/helpers/utility/CollectionFunctions";

import InfoPanel from "@editor/ui/InfoPanel.vue";

import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import InputCurve from "../relation/InputCurve.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useImageStore } from "../image/images.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import GraphModel from "../relation/GraphModel";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { MomentumItem } from "./MomentumItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";

const props = defineProps<{
  momentum: MomentumItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const imageStore = useImageStore();
const infoPanelStore = useInfoPanelStore();

const sections = reactive({
  description: true,
  element: true,
  interval: true,
  value: true,
  curve: true
});

const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true }
});

const targets = computed(() => {
  return useTargetStore().items;
});
const resources = computed(() => {
  return useResourceStore().items;
});
const indices = computed(() => {
  return useIndexStore().items;
});
const items = computed(() => {
  return {
    target: targets.value,
    resource: resources.value,
    index: indices.value
  };
});

// ELEMENT

const element = computed(() => {
  if (!props.momentum) return null;
  return items.value[props.momentum.elementType][props.momentum.elementId];
});

// CURVE

const curvePoints = computed(() => {
  if (!props.momentum) return [];
  return props.momentum.curvePoints;
});
const curveType = computed(() => {
  if (!props.momentum) return "linear";
  return props.momentum.curveType;
});

const graphModel = computed(() => {
  if (!props.momentum) return null;
  return new GraphModel(curvePoints.value);
});

const startValue = computed(() => {
  if (!graphModel.value) return 0;
  return graphModel.value.valueAtX(element.value?.value ?? 0);
});

// CURVE HOVER

const curveHover = reactive({
  x: 0,
  y: 0,
  x2: 0,
  y2: 0
});

function updateCurveHover(data: {
  x: number;
  y: number;
  x2: number;
  y2: number;
}) {
  curveHover.x = data.x;
  curveHover.y = data.y;
  curveHover.x2 = data.x2;
  curveHover.y2 = data.y2;
}

// IMAGE

const image = computed(() => {
  if (props.momentum && props.momentum.imageId != null) {
    return imageStore.items[props.momentum.imageId];
  }
  return null;
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}



function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.momentum = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.momentum || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as (keyof typeof sections)] = savedSections[key];
    }
  }
}

loadSections();


function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.momentum = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.momentum || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as (keyof typeof extraSettings)] = { ...(savedExtraSettings[key] as any) };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style>
.momentum-infopanel__curve {
  margin-top: 1rem;
  max-width: 20rem;
}
</style>
